import React, { createContext, useContext, useEffect, useState } from 'react';
import { onAuthStateChanged, signOut } from 'firebase/auth';
import { auth, database, storage } from '../../book/src/firebase';
import { ref as storageRef, uploadBytes, getDownloadURL } from 'firebase/storage';
import SpinnerOverlay from '../SpinnerOverlay';
import Papa from 'papaparse';
import {
  ref,
  get,
  onValue,
  off,
  onChildChanged,
  update,
  push,
  runTransaction,
  remove,
} from 'firebase/database';
import { useNavigate } from 'react-router';
import { translate } from 'src/translate';
import { isBizRegistrationFlow } from 'src/utils/postLoginRedirect';
import { setSentryUserContext } from 'src/utils/sentryContext';
import {
  SYSTEM_TAGS_PATH,
  canAssignTag,
  canUnassignTag,
  mapSystemTag,
} from 'src/utils/systemTags';

const AuthContext = createContext();

const AUTH_PHONE_STORAGE_KEY = 'wats_auth_phone';

const readStoredAuthPhone = () => {
  try {
    return sessionStorage.getItem(AUTH_PHONE_STORAGE_KEY) || '';
  } catch {
    return '';
  }
};

const writeStoredAuthPhone = (phone) => {
  try {
    if (phone) {
      sessionStorage.setItem(AUTH_PHONE_STORAGE_KEY, phone);
    } else {
      sessionStorage.removeItem(AUTH_PHONE_STORAGE_KEY);
    }
  } catch {
    // ignore storage errors
  }
};

const resolveUserPhone = async (user) => {
  if (!user) return '';
  const fromProfile =
    user.phoneNumber || user.providerData?.find((provider) => provider.phoneNumber)?.phoneNumber;
  if (fromProfile) return fromProfile;
  try {
    const token = await user.getIdTokenResult();
    const fromClaims =
      token.claims.phone_number || token.claims.phoneNumber || token.claims.phone;
    if (!fromClaims) return '';
    return String(fromClaims).startsWith('+') ? String(fromClaims) : `+${fromClaims}`;
  } catch {
    return '';
  }
};

const resolveIsSuperAdmin = async (user) => {
  if (!user) return false;
  try {
    const token = await user.getIdTokenResult();
    return token.claims.isSuperAdmin === true;
  } catch {
    return false;
  }
};

const findOwnerOrStaffIdAndAccessType = (obj, bizId) => {
  const keys = Object.keys(obj);
  if(bizId && (obj[bizId].accessType === 'OWNER' || obj[bizId].accessType === 'STAFF')) {
    return { id: bizId, accessType: obj[bizId].accessType };
  }
  for (let i = 0; i < keys.length; i++) {
    const key = keys[i];
    const accessType = obj[key].accessType;
    if (accessType === 'OWNER' || accessType === 'STAFF') {
      return { id: key, accessType };
    }
  }
  return { id: null, accessType: 'NORMAL' };
};

const findAdmins = (obj) => {
  const admins = [];
  const keys = Object.keys(obj);
  for (let i = 0; i < keys.length; i++) {
    const key = keys[i];
    const accessType = obj[key].accessType;
    if (accessType === 'OWNER' || accessType === 'STAFF') {
      admins.push({ id: key, accessType });
    }
  }
  return admins;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [phoneNumber, setPhoneNumber] = useState(readStoredAuthPhone);
  const [authMethod, setAuthMethod] = useState('whatsapp'); // Default to WhatsApp

  const [businessInfo, setBusinessInfo] = useState(null);
  const [accessType, setAccessType] = useState(null);
  const [customers, setCustomers] = useState(null);
  const [bizTags, setBizTags] = useState([]);
  const [systemTags, setSystemTags] = useState([]);
  const [tags, setTags] = useState([]);
  const [admins, setAdmins] = useState([]);
  const [bizNames, setBizNames] = useState([]);

  const [features, setFeatures] = useState({});
  const [additionalConfigs, setAdditionalConfigs] = useState(null);

  // Messaging: generic config (limits, templates, etc.)
  const [messagesGeneric, setMessagesGeneric] = useState(null);

  const [genericDetailsColumns, setGenericDetailsColumns] = useState(null);

  const [customersTagsCount, setCustomersTagsCount] = useState({});

  const [hiddenTagsKeys, setHiddenTagsKeys] = useState({});
  const [isSuperAdmin, setIsSuperAdmin] = useState(false);

  const navigate = useNavigate();

  const [loginTimeStamp, setLoginTimeStamp] = useState(() => {
    const savedTimeStamp = localStorage.getItem('loginTimeStamp');
    return savedTimeStamp ? new Date(savedTimeStamp) : null;
  });

  const [attemptCounter, setAttemptCounter] = useState(() => {
    const savedAttempts = localStorage.getItem('attemptCounter');
    return savedAttempts ? parseInt(savedAttempts, 10) : 0;
  });

  const resetAttemptCounter = () => {
    const newAttemptCount = 0;
    setAttemptCounter(newAttemptCount);
    localStorage.setItem('attemptCounter', newAttemptCount.toString());
  };

  const updateLoginTimeStamp = () => {
    const newTimeStamp = new Date();
    setLoginTimeStamp(newTimeStamp);
    localStorage.setItem('loginTimeStamp', newTimeStamp);

    // Update attempt counter
    const newAttemptCount = attemptCounter + 1;
    setAttemptCounter(newAttemptCount);
    localStorage.setItem('attemptCounter', newAttemptCount.toString());
  };

  const [bizId, setBizId] = useState(() => {
    // Initial state is fetched from localStorage if available
    return localStorage.getItem('bizId');
  });

  useEffect(() => {
    // Whenever bizId changes, update localStorage
    if (bizId !== null) {
      localStorage.setItem('bizId', bizId);
    }
  }, [bizId]);

  // Business name cache utilities
  const BIZ_NAMES_CACHE_KEY = 'wats_biz_names_cache';

  const getBizNameFromCache = (businessId) => {
    try {
      const cache = localStorage.getItem(BIZ_NAMES_CACHE_KEY);
      if (cache) {
        const parsedCache = JSON.parse(cache);
        return parsedCache[businessId] || null;
      }
    } catch (error) {
      console.error('Error reading from cache:', error);
    }
    return null;
  };

  const updateBizNameInCache = (businessId, businessName) => {
    try {
      const cache = localStorage.getItem(BIZ_NAMES_CACHE_KEY);
      const parsedCache = cache ? JSON.parse(cache) : {};
      parsedCache[businessId] = businessName;
      localStorage.setItem(BIZ_NAMES_CACHE_KEY, JSON.stringify(parsedCache));
    } catch (error) {
      console.error('Error updating cache:', error);
    }
  };

  const getBizName = (businessId, useCache = true) => {
    // If cache is enabled, try to get from cache first
    if (useCache) {
      const cachedName = getBizNameFromCache(businessId);
      if (cachedName) {
        return cachedName;
      }
    }

    // Search in bizNames array
    const business = bizNames.filter((biz) => biz.id === businessId);
    if (business.length === 1) {
      const businessName = business[0]?.name;
      // Update cache with the found name
      updateBizNameInCache(businessId, businessName);
      return businessName;
    }

    return businessId;
  };

  const isFeatureEnabled = (featureName) => {
    if (features[featureName]) {
      return features[featureName].isEnabled;
    }

    return false;
  };

  // get all of the biz names that the admins have access to
  useEffect(() => {
    const fetchBizNamesForAdmins = async () => {
      try {
        const bizNamesArray = [];
        for (const businessId of admins) {
          const dbRef = ref(database, `/biz/userInfo/${businessId}`);
          const snapshot = await get(dbRef);

          if (snapshot.exists()) {
            const bizName = snapshot.val().bizName;
            bizNamesArray.push({ id: businessId, name: bizName });
            // Update cache for each business name
            updateBizNameInCache(businessId, bizName);
          } else {
          }
        }
        setBizNames(bizNamesArray);
      } catch (error) {
        console.error(error);
      }
    };
    // Whenever bizId changes, update localStorage
    if (admins.length > 1) {
      fetchBizNamesForAdmins();
    }
  }, [admins, bizId]);

  useEffect(() => {
    const fetchBizInfo = async () => {
      try {
        const dbRef = ref(database, `/biz/userInfo/${bizId}`);
        const snapshot = await get(dbRef);

        if (snapshot.exists()) {
          setBusinessInfo(snapshot.val());
        } else {
        }
      } catch (error) {
        console.error(error);
      }
    };

    // Whenever bizId changes, update localStorage

    if (bizId !== null) {
      fetchBizInfo();
    }
  }, [bizId]);

  const updateBusinessDescription = async (description) => {
    if (!bizId) return;

    const nextDescription = description || null;
    const dbRef = ref(database, `/biz/userInfo/${bizId}`);

    try {
      await update(dbRef, { description: nextDescription });
      setBusinessInfo((prev) => ({
        ...(prev || {}),
        description: nextDescription,
      }));
    } catch (error) {
      console.error('Failed to update business description', error);
      throw error;
    }
  };

  useEffect(() => {
    const fetchUserAccessType = async (user) => {
      let bizzId = null;
      try {
        const dbRef = ref(database, `/users/businesses/${user.uid}`);
        const snapshot = await get(dbRef);

        if (snapshot.exists()) {
          const val = snapshot.val();

          const { id, accessType } = findOwnerOrStaffIdAndAccessType(val, bizId);

          if (accessType === 'NORMAL') {
            if (isBizRegistrationFlow()) {
              setAccessType('NORMAL');
              return;
            }
            localStorage.removeItem('bizId');
            signOut(auth); // log the user out
            navigate('/auth/404'); // redirect to 404 because snapshot does
            return;
          }

          const admins = findAdmins(val);
          const adminsArray = admins.map((admin) => admin.id);
          setAdmins(adminsArray);

          // in case we have super admin we dont want to override the option he chosed
          if (admins.length > 1 && bizId) {
            const findAdmin = admins.filter((admin) => admin.id === bizId);
            // in case we dont find the bizId in admins
            if (findAdmin.length === 0) {
              setBizId(admins[0].id);
            }

            // do nothing for now
          } else {
            setBizId(id);
          }

          bizzId = id;

          setAccessType(accessType);

          // setUserStatus(Object.values(val)[0]);
          // setUserStatus(val);
          // displayAdminMessageIfNeeded(val);
          // console.log(val);
        } else {
          if (isBizRegistrationFlow()) {
            setAccessType('NORMAL');
            return;
          }
          localStorage.removeItem('bizId');
          signOut(auth); // log the user out
          navigate('/auth/404'); // redirect to 404 because snapshot does
          return;
        }
      } catch (error) {
        console.error(error);
      }
    };

    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (user) {
        setUser(user);
        const [resolvedPhone, superAdmin] = await Promise.all([
          resolveUserPhone(user),
          resolveIsSuperAdmin(user),
        ]);
        if (resolvedPhone) {
          setPhoneNumber(resolvedPhone);
          writeStoredAuthPhone(resolvedPhone);
        }
        setIsSuperAdmin(superAdmin);
        fetchUserAccessType(user);
      } else {
        setUser(null);
        setPhoneNumber('');
        writeStoredAuthPhone('');
        setIsSuperAdmin(false);
      }
      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  useEffect(() => {
    if (user) {
      setSentryUserContext({
        uid: user.uid,
        bizId,
        accessType,
        email: user.email,
      });
    } else {
      setSentryUserContext({});
    }
  }, [user, bizId, accessType]);

  useEffect(() => {
    if (bizId) {
      const dbRef = ref(database, `/biz/userData/${bizId}`);

      const listener = onValue(
        dbRef,
        (snapshot) => {
          if (snapshot.exists()) {
            const info = snapshot.val();

            let formattedInfo = Object.entries(info).map(([id, details]) => ({
              id,
              ...details,
            }));
            // formattedInfo = formattedInfo.filter((element) => element.name);

            const parsedInfo = formattedInfo.map((customerInfo) => {
              // parse the time stamps
              try {
                if (customerInfo.number?.startsWith('9725')) {
                  customerInfo.numberLocal = customerInfo.number.replace(/^972/, '0');
                }
              } catch (e) {}

              if (customerInfo.prevEvent) {
                customerInfo.prevEventTime = customerInfo.prevEvent.startTimestamp;
              }

              if (customerInfo.nextEvent) {
                customerInfo.nextEventTime = customerInfo.nextEvent.startTimestamp;
              }

              return customerInfo;
            });

            setCustomers(parsedInfo);
          } else {
            // Handle the case where there is no data at the path
            console.log('No data available customers');
            setCustomers([]); // Or however you wish to handle this case
          }
        },
        (error) => {
          console.error(error);
        },
      );

      // Clean up the listener when the component unmounts or bizId changes
      return () => listener();
    }
  }, [bizId, database, tags]);

  useEffect(() => {
    if (bizId) {
      const dbRef = ref(database, `/biz/configs/${bizId}/features`);

      const listener = onValue(
        dbRef,
        (snapshot) => {
          if (snapshot.exists()) {
            const info = snapshot.val();
            setFeatures(info);

            // const xx = 'xx';

            // let formattedInfo = Object.entries(info).map(([id, details]) => ({
            //     id,
            //     ...details
            // }));
            // formattedInfo = formattedInfo.filter(element => element.name);

            // const parsedInfo = formattedInfo.map(customerInfo => {

            // // parse the time stamps

            //     return customerInfo;
            // })

            // setRows(formattedInfo);
            // console.log(formattedInfo);
          } else {
            setFeatures({});
            // Handle the case where there is no data at the path
            // console.log('No data available customers');
          }
        },
        (error) => {
          console.error(error);
        },
      );

      // Clean up the listener when the component unmounts or bizId changes
      return () => listener();
    }
  }, [bizId, database]);

  // Load additional configs: /biz/configs/${bizId}/additionalConfigs
  useEffect(() => {
    if (bizId) {
      setAdditionalConfigs(null);
      const dbRef = ref(database, `/biz/configs/${bizId}/additionalConfigs`);

      const listener = onValue(
        dbRef,
        (snapshot) => {
          if (snapshot.exists()) {
            const info = snapshot.val();
            setAdditionalConfigs(info);
          } else {
            setAdditionalConfigs({});
          }
        },
        (error) => {
          console.error(error);
        },
      );

      // Clean up the listener when the component unmounts or bizId changes
      return () => listener();
    } else {
      setAdditionalConfigs(null);
    }
  }, [bizId, database]);

  // Load generic messaging config: /biz/messages/generic/{bizId}
  useEffect(() => {
    if (bizId) {
      const dbRef = ref(database, `/biz/messages/generic/${bizId}`);

      const listener = onValue(
        dbRef,
        (snapshot) => {
          if (snapshot.exists()) {
            setMessagesGeneric(snapshot.val());
          } else {
            setMessagesGeneric(null);
          }
        },
        (error) => {
          console.error(error);
        },
      );

      return () => listener();
    }
  }, [bizId, database]);

  React.useEffect(() => {
    const dbRef = ref(database, SYSTEM_TAGS_PATH);
    const unsubscribe = onValue(
      dbRef,
      (snapshot) => {
        const info = snapshot.val();
        if (!info) {
          setSystemTags([]);
          return;
        }
        setSystemTags(Object.keys(info).map((key) => mapSystemTag(key, info[key])));
      },
      (error) => {
        console.error(error);
      },
    );

    return () => unsubscribe();
  }, [database]);

  React.useEffect(() => {
    const fetchBizTags = () => {
      const dbRef = ref(database, `/biz/tags/${bizId}/users`);

      const handleSnapshot = (snapshot) => {
        processSnapshot(snapshot);
      };

      const handleChildChanged = () => {
        get(dbRef).then((fullSnapshot) => {
          processSnapshot(fullSnapshot);
        });
      };

      const processSnapshot = (snapshot) => {
        try {
          const info = snapshot.val();
          let transformedTags = [];

          if (info) {
            transformedTags = Object.keys(info)
              ?.map((key) => ({
                value: key,
                label: info[key].name,
                color: info[key].color,
                isHidden: info[key].isHidden ?? false,
                isEditable: info[key].isEditable ?? true,
                isSystemTag: false,
                isAssignable: true,
                isUnassignable: true,
              }))
              .sort((a, b) => {
                const colorA = (a.color || '').replace('#', '').toLowerCase();
                const colorB = (b.color || '').replace('#', '').toLowerCase();
                return colorA.localeCompare(colorB);
              });
          }

          setBizTags(transformedTags);
        } catch (error) {
          console.error(error);
        }
      };

      onValue(dbRef, handleSnapshot, {
        onlyOnce: false,
      });

      onChildChanged(dbRef, handleChildChanged, {
        onlyOnce: false,
      });
    };

    if (bizId) {
      fetchBizTags();
    } else {
      setBizTags([]);
    }

    return () => {
      if (!bizId) return;
      const dbRef = ref(database, `/biz/tags/${bizId}/users`);
      off(dbRef, 'value');
      off(dbRef, 'child_changed');
    };
  }, [bizId]);

  useEffect(() => {
    const bizTagIds = new Set((bizTags || []).map((tag) => tag.value));
    const merged = [...(systemTags || []).filter((tag) => !bizTagIds.has(tag.value)), ...(bizTags || [])];
    const hiddenTagsKeys = merged.reduce((obj, tag) => {
      if (tag.isHidden) {
        obj[tag.value] = true;
      }
      return obj;
    }, {});
    setHiddenTagsKeys(hiddenTagsKeys);
    setTags(merged);
  }, [bizTags, systemTags]);

  useEffect(() => {
    const counterObject = {};

    if (tags && tags.length > 0 && customers) {
      tags.forEach((tag) => {
        counterObject[tag.value] = 0;
      });

      customers.forEach((customer) => {
        if (customer.tags && customer.tags.length > 0) {
          customer.tags.forEach((tag) => {
            const tagKey = Object.keys(tag)[0];
            counterObject[tagKey] = counterObject[tagKey] + 1;
          });
        }
      });

      setCustomersTagsCount(counterObject);
    }
  }, [tags, customers]);

  // useEffect(() => {
  //     const fetchCustomers = async (user) => {
  //         try {
  //             const dbRef = ref(database, `/users/businesses`);
  //             const snapshot = await get(dbRef);

  //             if (snapshot.exists()) {
  //                 const customersUnfiltered = snapshot.val();

  //                 const filteredCustomers = Object.entries(customersUnfiltered).reduce((acc, [customerId, businesses]) => {
  //                     // Check if the customer has the specified businessId
  //                     if (businesses.hasOwnProperty(bizId)) {
  //                         // Add customer to the accumulator with minimal data
  //                         acc[customerId] = {
  //                             // [bizId]: {
  //                             accessType: businesses[bizId].accessType,
  //                             blacklist: businesses[bizId].blacklist,
  //                             defaultLanguage: businesses[bizId].defaultLanguage,
  //                             displayName: businesses[bizId].displayName,
  //                             firstAppearance: businesses[bizId].firstAppearance
  //                             // }
  //                         };
  //                     }
  //                     return acc;
  //                 }, {});

  //                 setCustomers(filteredCustomers);
  //                 console.log(filteredCustomers);

  //             } else {
  //                 return;
  //             }

  //         } catch (error) {
  //             console.error(error);
  //         }
  //     };

  //     if (user && bizId) {
  //         fetchCustomers()
  //     }

  // }, [bizId]);

  // React.useEffect(() => {
  //     if (user) {
  //         const fetchUserAccessType = async () => {
  //             try {
  //                 const dbRef = ref(database, `/users/businesses/${user.uid}`);
  //                 const snapshot = await get(dbRef);

  //                 if (snapshot.exists()) {
  //                     const val = snapshot.val();

  //                     const {id, accessType} = findOwnerOrStaffIdAndAccessType(val);
  //                     setUserId(id);
  //                     setAccessType(accessType);

  //                     // setUserStatus(Object.values(val)[0]);
  //                     // setUserStatus(val);
  //                     // displayAdminMessageIfNeeded(val);
  //                     console.log(val);

  //                 } else {

  //                 }
  //             } catch (error) {
  //                 console.error(error);
  //             }
  //         };

  //         fetchUserAccessType();
  //     } else {
  //         // setUserStatus(null);
  //     }
  // }, []);

  const updatePhoneNumber = (newPhoneNumber) => {
    setPhoneNumber(newPhoneNumber);
    writeStoredAuthPhone(newPhoneNumber);
  };

  const updateAuthMethod = (method) => {
    setAuthMethod(method);
  };

  const formatPhoneNumber = (phoneNumber) => {
    let formatted = phoneNumber;
    if (formatted.startsWith('0')) {
      formatted = '+972' + formatted.substring(1);
    } else if (!formatted.startsWith('+')) {
      formatted = '+' + formatted;
    }
    return formatted;
  };

  const getCustomerUid = (phoneNumber) => {
    if (customers === null) return null;
    const customersFiltered = customers.filter((customer) => customer.number === phoneNumber);
    if (customersFiltered.length === 0) return null;
    return customersFiltered[0].id;
  };

  const getCustomerFromNumber = (phoneNumber) => {
    if (customers === null) return null;
    const customersFiltered = customers.filter((customer) => customer.number === phoneNumber);
    if (customersFiltered.length === 0) return null;
    return customersFiltered[0];
  };

  const getCustomer = (uid) => {
    if (customers === null) return null;
    const customersFiltered = customers.filter((customer) => customer.id === uid);
    if (customersFiltered.length === 0) return null;
    return customersFiltered[0];
  };

  // tags and notes context

  const getUserAvailableTags = (tags, answer) => {
    try {
      if (!tags) {
        return [];
      }

      const answerKeys = answer ? answer?.map((obj) => Object.keys(obj)[0]) : [];

      if (!answerKeys) {
        return [];
      }

      // Filter tags not in answer and format them in an array of objects
      return tags?.reduce((acc, tag) => {
        if (!canAssignTag(tag)) {
          return acc;
        }
        if (!answerKeys.includes(tag.value)) {
          const obj = {};
          obj[tag.value] = 1; // Set timestamp to 1
          acc.push(obj);
        }
        return acc;
      }, []);
    } catch (err) {
      console.log(err);
      return [];
    }
  };

  const updateTags = (customerId, answerId, tagId, tags, operation) => {
    const knownTag = (bizTags || []).concat(systemTags || []).find((tag) => tag.value === tagId);

    if (operation === 'delete' && !canUnassignTag(knownTag)) {
      return;
    }
    if (operation !== 'delete' && !canAssignTag(knownTag)) {
      return;
    }

    // if there isnt any tag, init an empty array
    if (!tags) {
      tags = [];
    }

    let filteredTags;
    const timeAdded = new Date();

    // if its delete op, we delete the relevant tag
    if (operation === 'delete') {
      filteredTags = tags.filter((tag) => !(tagId in tag));
    }
    // if its add operation, we add to the tags
    else {
      tags.push({ [tagId]: timeAdded.getTime() });
      filteredTags = tags;
    }

    const dbRef = ref(database, `biz/userData/${bizId}/${customerId}`);

    try {
      update(dbRef, { tags: filteredTags });
      // console.log('Working hours saved successfully.');
    } catch (error) {
      console.error('Failed to update tag', error);
    }
  };

  const addTag = (tagName, tagColor) => {
    const timeAdded = new Date();
    const dbRef = ref(database, `/biz/tags/${bizId}/users`);

    if (bizId === 'b7cbcb6a-5c35-49e7-b254-08f774245601') {
      if (user.uid !== '84417988-c251-41d9-ade3-bdabbec17542') {
        return;
      }
    }

    try {
      const tagRef = push(dbRef, {
        createdTimestamp: timeAdded.getTime(),
        name: tagName,
        color: tagColor,
      });
      const tagId = tagRef.key;
      return tagId;
      // console.log('Working hours saved successfully.');
    } catch (error) {
      console.error('Failed to update tag', error);
    }
  };

  // Function to edit or delete a tag based on the operation specified
  const modifyTag = async (operation, tagId, tagName = null, tagColor = null) => {
    const knownTag = (bizTags || []).concat(systemTags || []).find((tag) => tag.value === tagId);
    if (knownTag?.isSystemTag) {
      return;
    }

    const dbRef = ref(database, `/biz/tags/${bizId}/users/${tagId}`);

    if (bizId === 'b7cbcb6a-5c35-49e7-b254-08f774245601') {
      if (user.uid !== '84417988-c251-41d9-ade3-bdabbec17542') {
        return;
      }
    }

    try {
      if (operation === 'edit') {
        // Check if both tagName and tagColor are provided for editing
        if (tagName === null || tagColor === null) {
          console.error('Both tag name and color must be provided for editing.');
          return;
        }
        // Perform update operation
        await update(dbRef, { name: tagName, color: tagColor });
      } else if (operation === 'delete') {
        // Perform delete operation
        await update(dbRef, { name: tagName, color: tagColor, isHidden: true });
      } else {
        console.error('Invalid operation. Please specify "edit" or "delete".');
      }
    } catch (error) {
      console.error(`Failed to ${operation} tag`, error);
    }
  };

  // Example usage:
  // modifyTag('edit', 'someTagId', 'New Tag Name', 'New Tag Color');
  // modifyTag('delete', 'someTagId');

  const uploadNoteImage = async (file, customerId) => {
    if (!file || !bizId) {
      throw new Error('File and business ID are required for uploading.');
    }
    const timestamp = Date.now();
    const filePath = `/biz/${bizId}/notes/${customerId}/${timestamp}_${file.name}`;
    const fileRef = storageRef(storage, filePath);
    const uploadResult = await uploadBytes(fileRef, file);
    const url = await getDownloadURL(uploadResult.ref);
    return { url, path: filePath, name: file.name };
  };

  const addNote = (customerId, note, images = [], isImportant = false) => {
    const timeAdded = new Date();
    const notesRef = ref(database, `/biz/userData/${bizId}/${customerId}/notes`);

    const formattedNote = note.replace(/\n/g, '<br />');
    const noteObj = { timestamp: timeAdded.getTime(), note: formattedNote, color: 'blue' };
    if (images.length > 0) {
      noteObj.images = images;
    }
    if (isImportant) {
      noteObj.isImportant = true;
    }

    runTransaction(notesRef, (currentNotes) => {
      if (currentNotes === null) {
        return [noteObj];
      } else {
        currentNotes.push(noteObj);
        return currentNotes;
      }
    })
      .then((result) => {
        if (result.committed) {
          console.log('Note added successfully.');
        } else {
          console.log('Transaction not committed.');
        }
      })
      .catch((error) => {
        console.error('Transaction failed:', error);
      });

    return noteObj;
  };

  const removeNote = (customerId, timestampToRemove) => {
    const notesRef = ref(database, `/biz/userData/${bizId}/${customerId}/notes`);

    // Use a transaction to safely remove a note
    runTransaction(notesRef, (currentNotes) => {
      if (currentNotes === null) {
        // If there are no notes, there's nothing to remove
        return; // No need to update the notes array
      } else {
        // Filter out the note with the specific timestamp
        const filteredNotes = currentNotes.filter((note) => note.timestamp !== timestampToRemove);
        return filteredNotes;
      }
    })
      .then((result) => {
        if (result.committed) {
          console.log('Note removed successfully.');
        } else {
          console.log('Transaction not committed, no changes made.');
        }
      })
      .catch((error) => {
        console.error('Transaction failed:', error);
      });
  };

  const editNote = (customerId, timestamp, newNoteText, images, isImportant = false) => {
    const timeEdited = new Date();
    const notesRef = ref(database, `/biz/userData/${bizId}/${customerId}/notes`);

    const formattedNote = newNoteText.replace(/\n/g, '<br />');
    const editedAtText = `<br /><br />Edited At ${timeEdited.toLocaleString()}`;
    const finalNoteText = formattedNote + editedAtText;

    runTransaction(notesRef, (currentNotes) => {
      if (currentNotes === null) {
        return null;
      } else {
        return currentNotes.map((note) => {
          if (note.timestamp === timestamp) {
            const updated = { ...note, note: finalNoteText };
            if (images !== undefined) {
              updated.images = images.length > 0 ? images : null;
            }
            if (isImportant) {
              updated.isImportant = true;
            } else {
              delete updated.isImportant;
            }
            return updated;
          }
          return note;
        });
      }
    })
      .then((result) => {
        if (result.committed) {
          console.log('Note edited successfully.');
        } else {
          console.log('Transaction not committed, no changes made.');
        }
      })
      .catch((error) => {
        console.error('Transaction failed:', error);
      });
  };

  // business details context

  const changeCustomerDefaultLanguage = (customerId, newLanguage) => {
    const dbRef = ref(database, `users/businesses/${customerId}/${bizId}`);

    try {
      update(dbRef, { defaultLanguage: newLanguage });
    } catch (error) {
      console.error('Failed to update customer default language', error);
    }
  };

  const changeCustomerName = (customerId, newName) => {
    const dbRef = ref(database, `biz/userData/${bizId}/${customerId}`);

    try {
      update(dbRef, { name: newName });
      // console.log('Working hours saved successfully.');
    } catch (error) {
      console.error('Failed to customer name', error);
    }

    const dbRefName = ref(database, `users/businesses/${customerId}/${bizId}`);

    try {
      update(dbRefName, { displayName: newName });
      // console.log('Working hours saved successfully.');
    } catch (error) {
      console.error('Failed to customer name', error);
    }
  };

  const updateCustomerGenericDetails = (customerId, newGenericDetails) => {
    const dbRef = ref(database, `biz/userData/${bizId}/${customerId}`);

    try {
      update(dbRef, { genericDetails: newGenericDetails });
      // console.log('Working hours saved successfully.');
    } catch (error) {
      console.error('Failed to customer name', error);
    }
  };

  useEffect(() => {
    const fetchGenericDetailsColumns = () => {
      const dbRef = ref(database, `/biz/configs/${bizId}/genericDetails`);

      const onValueChange = onValue(
        dbRef,
        (snapshot) => {
          if (snapshot.exists()) {
            setGenericDetailsColumns(snapshot.val());
          } else {
            // Handle the case where there is no data
            setGenericDetailsColumns(null);
          }
        },
        (error) => {
          console.error(error);
        },
      );

      return () => off(dbRef, 'value', onValueChange);
    };

    if (bizId) {
      const unsubscribe = fetchGenericDetailsColumns();
      return unsubscribe;
    }
  }, [bizId]);

  const getGenericDetailsColumnHeader = (headerName, language) => {
    if (!genericDetailsColumns) {
      return 'Error';
    }

    if (genericDetailsColumns[headerName][language]) {
      return genericDetailsColumns[headerName][language];
    }
    return genericDetailsColumns[headerName][Object.keys(genericDetailsColumns[headerName])[0]];
  };

  const exportToCsv = (sortedCustomers, language) => {
    try {
      const csvArray = [];

      // Collect all unique keys from genericDetails
      const allKeys = new Set();
      sortedCustomers.forEach((customer) => {
        if (customer.genericDetails) {
          Object.keys(customer.genericDetails).forEach((key) => {
            allKeys.add(key);
          });
        }
      });

      for (const customer of sortedCustomers) {
        const genericDetails = customer.genericDetails || {};

        const objectToAdd = {};

        objectToAdd[translate('name', language)] = customer.name;
        objectToAdd[translate('phoneNumber', language)] = customer.number;
        objectToAdd[translate('uid', language)] = customer.id;

        // Add each detail or a placeholder if missing
        allKeys.forEach((key) => {
          const header = getGenericDetailsColumnHeader(key, language);
          objectToAdd[header] = genericDetails[key] ? genericDetails[key].toString() : '-';
        });

        csvArray.push(objectToAdd);
      }

      // Function to collect all unique question keys from the dataset

      // const allQuestionKeys = Array.from(getAllQuestionKeys());

      // const processedData = sortedAnswers.map((item) => {
      //   // Fill in each question, using a dash "-" where data is missing

      //   const filledQuestions = allQuestionKeys.reduce((acc, key) => {
      //     const headerKey = Utils.getLanguageOrDefault({ strings: questions[key] }, language);

      //     acc[headerKey] = item.questions[key] ? item.questions[key].val : '-';
      //     return acc;
      //   }, {});

      //   return {
      //     ...filledQuestions,

      //     [translate('phoneNumber', language)]: getCustomer(item.customerId)?.number,
      //     [translate('name', language)]: getCustomer(item.customerId)?.name,
      //   };
      // });

      // const processedData = { name: 'Name', phone: 'number' };

      const csv = Papa.unparse(csvArray, {
        header: true,
        delimiter: ',', // You can specify a custom delimiter
      });

      // Create a Blob from the CSV String
      // Create a Blob with UTF-8 encoding and BOM
      const BOM = '\uFEFF';
      const csvBlob = new Blob([BOM + csv], { type: 'text/csv;charset=utf-8;' });
      const csvUrl = URL.createObjectURL(csvBlob);

      return csvUrl;
    } catch (err) {
      return null;
    }
  };

  const isAHLamasat = () => {
    return (
      bizId === '7ed2d810-d356-422f-9fc5-bc201e66116c' ||
      bizId === '0c51e037-e539-405e-99d5-64a7738df09f'
    );
  };

  const isRamiMolla = () => {
    return bizId === 'b7cbcb6a-5c35-49e7-b254-08f774245601';
  };

  const isCoachAws = () => {
    return bizId === '01e7e609-39e4-4efe-9674-f041ab5e1d97';
  };

  const isGalilRiders = () => {
    return bizId === 'feeb3f32-49b7-4ac6-9c5f-fb3bd53896a3';
  };

  const archiveCustomer = (customerId) => {
    const dbRef = ref(database, `biz/userData/${bizId}/${customerId}`);
    try {
      update(dbRef, { isHidden: true });
    } catch (error) {
      console.error('Failed to archive customer', error);
    }
  };

  const unarchiveCustomer = (customerId) => {
    const dbRef = ref(database, `biz/userData/${bizId}/${customerId}`);
    try {
      update(dbRef, { isHidden: false });
    } catch (error) {
      console.error('Failed to unarchive customer', error);
    }
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        loading,
        phoneNumber,
        updatePhoneNumber,
        formatPhoneNumber,
        authMethod,
        updateAuthMethod,
        bizId,
        accessType,
        businessInfo,
        updateBusinessDescription,
        customers,
        getCustomerUid,
        getCustomer,
        tags,
        getUserAvailableTags,
        updateTags,
        addTag,
        admins,
        setBizId,
        getBizName,
        isFeatureEnabled,
        addNote,
        removeNote,
        editNote,
        uploadNoteImage,
        genericDetailsColumns,
        changeCustomerName,
        changeCustomerDefaultLanguage,
        updateCustomerGenericDetails,
        getGenericDetailsColumnHeader,
        updateLoginTimeStamp,
        loginTimeStamp,
        customersTagsCount,
        modifyTag,
        hiddenTagsKeys,
        exportToCsv,
        attemptCounter,
        resetAttemptCounter,
        isAHLamasat,
        isRamiMolla,
        isCoachAws,
        isGalilRiders,
        getCustomerFromNumber,
        archiveCustomer,
        unarchiveCustomer,
        messagesGeneric,
        additionalConfigs,
        isSuperAdmin,
      }}
    >
      {!loading ? children : <SpinnerOverlay />}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  return context || {};
};
